import * as Crypto from 'expo-crypto';
import { File, Directory, Paths } from 'expo-file-system';
import Toast from 'react-native-toast-message'
import { getCurrentOrgID } from '../data/organization';
import * as Sentry from '@sentry/react-native';
import ErrorHandler from './ErrorHandler';
import { supabase } from '../data/client';
import mime from 'mime'
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import * as VideoThumbnails from 'expo-video-thumbnails';
import { Platform, Image as RNImage } from 'react-native';
import { ImageManipulator } from 'expo-image-manipulator';
import * as Sharing from 'expo-sharing';
import FileViewer from 'react-native-file-viewer';

const storagePathPrivate = `${process.env.EXPO_PUBLIC_DB_URL}/storage/v1/object/authenticated/${process.env.EXPO_PUBLIC_STORAGE_BUCKET}`;
const storagePathPublic = `${process.env.EXPO_PUBLIC_DB_URL}/storage/v1/object/public/${process.env.EXPO_PUBLIC_STORAGE_BUCKET_PUBLIC}`;

let _cachedAccessToken = null;
supabase.auth.getSession().then(({ data: { session } }) => { _cachedAccessToken = session?.access_token ?? null; });
supabase.auth.onAuthStateChange((_event, session) => { _cachedAccessToken = session?.access_token ?? null; });

const clearFileCache = async () => {
    if(Platform.OS === 'web'){
        return true;
    }

    try {
        const docDir = Paths.document;
        const items = docDir.list();
        
        for (const item of items) {
            item.delete();
        }

        Toast.show({
            type: 'success',
            text2: `Cache Directory cleared`
        });

        return true;
    } catch (error) {
        console.debug("Error clearing cache", error);
        return false;
    }
}

const getImageSource = (key, cacheKey, publicBucket = false) => {
    if(!key){
        return null;
    }

    const isPhoto = key.includes(".jpg") || key.includes(".png") || key.includes(".gif") || key.includes(".webp") || key.includes(".jpeg") || key.includes(".heic");
    const isVideo = key.includes(".mp4") || key.includes(".mov") || key.includes(".webm");

    if(publicBucket || key.startsWith('http')){
        return {
            uri: `${storagePathPublic}/${key}`,
            type: isPhoto ? 'photo' : isVideo ? 'video' : 'other'
        }
    }
    else{
        return {
            uri: `${storagePathPrivate}/${key}${cacheKey ? '?cacheKey=' + cacheKey : ''}`,
            type: isPhoto ? 'photo' : isVideo ? 'video' : 'other',
            cacheKey: cacheKey,
            headers: {
                'Authorization': `Bearer ${_cachedAccessToken}`
            }
        }
    }
}


const getFile = async (key, checkStorage = true, includeInfo = false, _publicBucket = false) => {
    const orgID = await getCurrentOrgID();

    if(!key){
        return includeInfo ? {uri:null, exists: false, size: 0} : null;
    }

    if(!key.startsWith(orgID + "/")){
        key = orgID + "/" + key;
    }

    const localKey = formatPath(key);

    try{
        if(Platform.OS === 'web'){
            throw Error("Web platform - skip local file check")
        }

        const file = new File(Paths.document, localKey);
    
        if(!file.exists){
            if(checkStorage){
                const uploadedFile = await getStorageFile(key);

                if(uploadedFile){
                    if(includeInfo){
                        const newFile = new File(Paths.document, localKey);
                        return { uri: newFile.uri, exists: newFile.exists, size: newFile.size };
                    }
                    else{
                        return file.uri;
                    }
                }
            }
    
            return includeInfo ? {uri:null, exists: false, size: 0} : null;
        }
        else{
            return includeInfo ? { uri: file.uri, exists: file.exists, size: file.size } : file.uri;
        }
    }
    catch(e){
        console.debug("Error reading local file", e);
        if(checkStorage){
            return await getStorageFile(key, includeInfo);
        }
    }
}


const saveFile = async (source, key, publicBucket = false, mimeType = null) => {
    const {path, filename} = splitPath(key);
    const {newFilePath, error} = await saveLocalFile(source, path, filename);

    if(error){
        return false
    }

    const {storageUrl, error: storageError} = await saveStorageFile({key: key, contentPath: newFilePath, contentType: mimeType, publicBucket})

    if(storageError){
        return false
    }

    return {key: key, newFilePath: newFilePath, storageUrl}
}


const saveStorageBlob = async (source, key, publicBucket = false) => {
    const orgID = await getCurrentOrgID();

    if(!key) return false;

    if(!publicBucket && !key.startsWith(orgID + "/")){
        key = orgID + "/" + key;
    }

    try{
        const {error} = await supabase.storage.from(publicBucket ? process.env.EXPO_PUBLIC_STORAGE_BUCKET_PUBLIC : process.env.EXPO_PUBLIC_STORAGE_BUCKET).upload(key, source, {
            upsert: true,
            contentType: source.type,
        })

        if(error){
            ErrorHandler(Error(error.message), {key: key,  contentType: source.type, function: "saveStorageFile"}, `Something went wrong uploading the file to the server`)
            return {error: error}
        }
    
        return {error: null, key: key, storageUrl: (publicBucket ? storagePathPublic : storagePathPrivate) + "/" + key}
    }
    catch(error){
        ErrorHandler(error, {key: key, contentType: source.type, function: "saveStorageBlob"})
        throw Error(error);
    }
}


const saveLocalFile = async (source, path, filename = null, copy = false) => {
    if(Platform.OS === 'web'){
        return { error: null, key: path + "/" + filename, newFilePath: source };
    }

    const localPath = formatPath(path);

    try{
        if(!filename){
            let ext = source.split('.').pop();
            filename = Crypto.randomUUID() + "." + ext;
        }

        const directory = new Directory(Paths.document, localPath);
        if(!directory.exists){
            directory.create({ intermediates: true });
        }
    
        const destFile = new File(directory, formatPath(filename));
        const sourceFile = new File(source);

        if(!sourceFile.exists){
            return {error: new Error(`Source file does not exist: ${source}`)};
        }

        if(destFile.exists){
            destFile.delete();
        }

        if(copy){
            await sourceFile.copy(destFile);
        }
        else{
            await sourceFile.move(destFile);
        }
        
        return { error: null, key: path + "/" + filename, newFilePath: destFile.uri };
    }
    catch(error){
        ErrorHandler(error, {source: source, path: path, filename: filename, function: "saveLocalFile"})
        return {error: error}
    }
}


const deleteLocalFile = async (key) => {
    if(Platform.OS === 'web'){
        return true;
    }

    try {
        const file = new File(Paths.document, formatPath(key));
        if(file.exists){
            file.delete();
        }
        return true;
    } catch (error) {
        console.debug("Error deleting local file", error);
        return false;
    }
}


const removeStorageFile = async (key) => {
    const orgID = await getCurrentOrgID();
    const fullKey = key.startsWith(orgID + "/") ? key : orgID + "/" + key;

    const { error } = await supabase.storage
    .from(process.env.EXPO_PUBLIC_STORAGE_BUCKET)
    .remove(fullKey)

    if(error){
        ErrorHandler(error, {key: key, function: "removeStorageFile"}, `Something went wrong removing the file from the server`)
        return {error: error}
    }

    await deleteLocalFile(key);
    return true
}

const saveStorageFile = async ({key, contentPath, contentType, onProgress: _onProgress = () => {}, publicBucket = false}) => {
    const orgID = await getCurrentOrgID();

    if(!key) return false;

    if(!publicBucket && !key.startsWith(orgID + "/")){
        key = orgID + "/" + key;
    }

    try{
        let uploadData;
        let resolvedContentType = contentType;

        if(Platform.OS === 'web'){
            if(contentPath.startsWith('file://')){
                return {error: new Error(`Cannot upload a device-local file URI on web: ${contentPath}`)};
            }
            const response = await fetch(contentPath);
            const blob = await response.blob();
            // Use blob's native type as fallback if no contentType provided
            if(!resolvedContentType) resolvedContentType = blob.type || mime.getType(contentPath);
            uploadData = blob;
        } else {
            const file = new File(contentPath);
            // bytes() reads straight to a Uint8Array — avoids holding the whole
            // file as a base64 string plus a decoded ArrayBuffer (~2.3x size).
            const bytes = await file.bytes();
            uploadData = bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength
                ? bytes.buffer
                : bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
            if(!resolvedContentType) resolvedContentType = mime.getType(contentPath);
        }

        const {error} = await supabase.storage.from(publicBucket ? process.env.EXPO_PUBLIC_STORAGE_BUCKET_PUBLIC : process.env.EXPO_PUBLIC_STORAGE_BUCKET).upload(key, uploadData, {
            upsert: true,
            contentType: resolvedContentType,
        })

        if(error){
            ErrorHandler(Error(error.message), {key: key, contentPath: contentPath, contentType: mime.getType(contentPath), function: "saveStorageFile"}, `Something went wrong uploading the file to the server`)
            return {error: error}
        }
    
        return {error: null, key: key, storageUrl: (publicBucket ? storagePathPublic : storagePathPrivate) + "/" + key}
    }
    catch(error){
        // iOS cleans up temp files between pick and upload — "no such file" /
        // "couldn't be opened" is an expected lifecycle state, not a bug. Return
        // a user-friendly error instead of reporting to Sentry. See CHURCH-APP-16D.
        const msg = error?.message || '';
        if(msg.includes("couldn't be opened") || msg.includes('no such file') || msg.includes('ENOENT')){
            return {error: new Error('The selected file is no longer available. Please try again.')};
        }
        ErrorHandler(error, {key: key, contentPath: contentPath, contentType: mime.getType(contentPath), function: "saveStorageFile"})
        throw Error(error);
    }
}

const getPublicStorageFileURL = async (key) => {
    const { data, error } = await supabase.storage
    .from(process.env.EXPO_PUBLIC_STORAGE_BUCKET_PUBLIC)
    .getPublicUrl(key)

    if(error){
        ErrorHandler(error, {key: key, function: "getPublicStorageFileURL", error}, `Something went wrong fetching the file from the server`)
        return {error: error}
    }

    return data.publicUrl;
}


const getSignedStorageFileURL = async (key, expiresIn = 60) => {
    const orgID = await getCurrentOrgID();

    if(!key) return null;

    if(!key.startsWith(orgID + "/")){
        key = orgID + "/" + key;
    }

    const { data, error } = await supabase.storage
    .from(process.env.EXPO_PUBLIC_STORAGE_BUCKET)
    .createSignedUrl(key, expiresIn)

    if(error){
        ErrorHandler(error, {key: key, function: "getSignedStorageFileURL"}, `Something went wrong creating signed URL`)
        return null;
    }

    return data.signedUrl;
}


const getStorageFile = async (key, includeInfo = false) => {
    const orgID = await getCurrentOrgID();

    if(!key) return false;

    if(!key.startsWith(orgID + "/")){
        key = orgID + "/" + key;
    }

    try{
        if(Platform.OS === 'web'){
            const { data, error } = await supabase.storage
            .from(process.env.EXPO_PUBLIC_STORAGE_BUCKET)
            .download(key)

            if(error) throw error;

            const reader = new FileReader();
            reader.readAsDataURL(data);
            return new Promise(function (resolve, _reject) {
                reader.onloadend = async () => {
                    resolve(includeInfo ? {uri: reader.result, exists: true, size: data.size} : true)
                }
            })
        }

        //Native: bypass supabase.storage.download() which calls response.blob()
        //— "Creating blobs from ArrayBuffer not supported" on React Native.
        //Use XHR with arraybuffer response type instead (same pattern as uploads).
        const downloadUrl = `${storagePathPrivate}/${key}`;
        const arrayBuffer = await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('GET', downloadUrl, true);
            xhr.responseType = 'arraybuffer';
            if(_cachedAccessToken){
                xhr.setRequestHeader('Authorization', `Bearer ${_cachedAccessToken}`);
            }
            xhr.onload = () => {
                if(xhr.status >= 200 && xhr.status < 300){
                    resolve(xhr.response);
                } else {
                    reject(new Error(`Storage download failed: ${xhr.status}`));
                }
            };
            xhr.onerror = () => reject(new Error('Network error during storage download'));
            xhr.ontimeout = () => reject(new Error('Network request timed out'));
            xhr.send();
        });

        const bytes = new Uint8Array(arrayBuffer);
        const localKey = formatPath(key);
        const pathParts = localKey.split('/');
        const fileName = pathParts.pop();
        const dirPath = pathParts.join('/');

        const directory = new Directory(Paths.document, dirPath);
        if(!directory.exists){
            directory.create({ intermediates: true });
        }

        const file = new File(directory, fileName);
        file.write(bytes);

        return includeInfo ? {uri: file.uri, exists: true, size: arrayBuffer.byteLength} : true;
    }
    catch(error){
        console.debug("Storage read error: ", error.message, key);

        if(error.message == "Network request timed out"){
            Toast.show({type: "error", text2: "We're having trouble connecting to the server. Make sure you have an active cellular or wifi connection!"})
        }
        else{
            Sentry.captureException(error, {extra: {storagekey: key}});
        }

        return false
    }
}


const renderManipulatedImage = async ({uri, compress = 0.8, resize = null, context = 'renderManipulatedImage'}) => {
    let lastError = null;

    for(let attempt = 0; attempt < 2; attempt++){
        try{
            let renderable = ImageManipulator.manipulate(uri);
            if(resize){
                renderable = renderable.resize(resize);
            }
            const rendered = await renderable.renderAsync();
            return await rendered.saveAsync({compress});
        }
        catch(error){
            lastError = error;
        }
    }

    ErrorHandler(lastError, {uri: uri, context: context, function: 'renderManipulatedImage'});
    return null;
}

const choosePhoto = async ({noVideos = false, noPhotos = false, multiple = false, selectionLimit = 0, maxSize, quality = 0.2}) => {
    const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: noVideos ? ['images'] : (noPhotos ? ['videos'] : ['images', 'videos']),
        quality: 1,
        allowsMultipleSelection: multiple,
        selectionLimit: selectionLimit,
        videoQuality: ImagePicker.UIImagePickerControllerQualityType.Medium
    });

    const attachments = [];
    const isWeb = Platform.OS === 'web';

    if (!result.canceled) {
        for(let i=0; i<result.assets.length; i++){
            const new_attachment = {
                uri: result.assets[i].uri,
                type: result.assets[i].type,
                extension: result.assets[i].fileName?.split('.').pop() || 'jpg',
                size: result.assets[i].fileSize,
                name: result.assets[i].fileName || `image_${Date.now()}.jpg`
            }

            if(result.assets[i].type == 'video'){
                const { uri } = await VideoThumbnails.getThumbnailAsync(result.assets[i].uri, { time: 0 })
                new_attachment.thumbnailUri = uri;
            }
            else{
                // const origWidth = result.assets[i].width;
                // const origHeight = result.assets[i].height;
                // const maxDim = Math.max(origWidth, origHeight);
                // if(maxDim > 600){
                //     renderable = renderable.resize(origWidth >= origHeight ? { width: 600 } : { height: 600 });
                // }
                const finalImage = await renderManipulatedImage({uri: result.assets[i].uri, compress: quality, context: 'choosePhoto'});

                if(finalImage){
                    new_attachment.thumbnailUri = finalImage.uri
                    new_attachment.uri = finalImage.uri

                    if(!isWeb){
                        const file = new File(finalImage.uri);
                        new_attachment.size = file.size;
                    }
                    else{
                        const response = await fetch(finalImage.uri)
                        const blob = await response.blob()
                        new_attachment.size = blob.size
                        new_attachment.blob = blob
                    }

                    if(maxSize){
                        const maxSizeDivisor = Math.max(finalImage.width, finalImage.height) / maxSize;

                        new_attachment.height = finalImage.height / maxSizeDivisor
                        new_attachment.width = finalImage.width / maxSizeDivisor
                    }
                }
                else{
                    new_attachment.thumbnailUri = result.assets[i].uri
                    new_attachment.uri = result.assets[i].uri
                }
            }

            attachments.push(new_attachment)
        }
    }

    return attachments;
};


const getMediaFileInfo = async (uri, sizeDivisor = 250) => {
    if(!uri) return {};

    if(uri.startsWith("data:")){
        const mediaType = uri.slice(5, uri.indexOf(';')).toLowerCase();
        const isVideo = mediaType.startsWith("video/");
        const isPhoto = mediaType.startsWith("image/");

        return {
            uri: uri,
            type: isVideo ? "video" : (isPhoto ? "photo" : "mixed"),
            extension: mime.getExtension(mediaType) || (isPhoto ? "jpg" : "bin"),
            thumbnailUri: isPhoto ? uri : undefined
        }
    }

    const ext = uri.split('.').pop();
    const type = mime.getType(ext);
    const isVideo = type?.startsWith("video/");
    const isPhoto = type?.startsWith("image/");

    const attachment = {
        uri: uri,
        type: isPhoto ? "photo" : (isVideo ? "video" : "mixed"),
        extension: ext,
    }

    if(isVideo){
        try{
            const { uri: thumbnail } = await VideoThumbnails.getThumbnailAsync(uri, { time: 0 })
            attachment.thumbnailUri = thumbnail;
        }
        catch{
            attachment.thumbnailUri = undefined;
        }
    }
    else{
        attachment.thumbnailUri = uri
    }

    if(!attachment.thumbnailUri) return attachment;

    const sizing = await new Promise((resolve) => {
        RNImage.getSize(attachment.thumbnailUri, (imageWidth, imageHeight) => {
            try{
                let maxSizeDivisor = Math.max(imageWidth, imageHeight) / sizeDivisor;

                resolve({
                    originalHeight: imageHeight,
                    originalWidth: imageWidth,
                    height: imageHeight / maxSizeDivisor,
                    width: imageWidth / maxSizeDivisor
                })
            }
            catch{
                resolve({}) //Size unknown — MediaViewer will use fallback dimensions
            }
        }, () => {
            resolve({}) //Image failed to load — return empty sizing so the caller can proceed
        })
    })

    return {...attachment, ...sizing}

}

const formatSize = (bytes) => {
    const kb = bytes / 1024;
    const mb = kb / 1024;
    const gb = mb / 1024;
    const size = bytes < 1000 
        ? bytes.toFixed(0) + " bytes" 
        : kb < 1000 
            ? kb.toFixed(0) + " KB" 
            : mb < 1000 
                ? mb.toFixed(1) + " MB" 
                : gb.toFixed(1) + " GB";

    return size.slice(-2) == ".0" ? size.slice(0, -2) : size;
}


const formatPath = (input) => {
    if(!input){
        return input;
    }

    const replaced = input.replace(/[^a-zA-Z0-9_./-]+/g, '_');
    return replaced;
}


const splitPath = (filePath) => {
    if(!filePath){
        return { path: '', filename: '' };
    }

    const lastSlashIndex = filePath.lastIndexOf('/');
    if (lastSlashIndex === -1) {
      // If no slashes found, return the whole string as filename
      return { path: '', filename: filePath };
    } else {
      const path = filePath.substring(0, lastSlashIndex + 1); // Include the last slash
      const filename = filePath.substring(lastSlashIndex + 1);
      return { path, filename };
    }
  };

// New helpers for Files v2

const sanitizeFilename = (name) => {
    if (!name) return '';
    
    // Remove disallowed characters, collapse whitespace
    let sanitized = name.replace(/[^a-zA-Z0-9 _\-.()]/g, '_').replace(/\s+/g, ' ').trim();
    
    // Limit to 200 characters
    if (sanitized.length > 200) {
        sanitized = sanitized.substring(0, 200);
    }
    
    // Ensure extension is lowercase
    const lastDotIndex = sanitized.lastIndexOf('.');
    if (lastDotIndex > 0) {
        const base = sanitized.substring(0, lastDotIndex);
        const ext = sanitized.substring(lastDotIndex).toLowerCase();
        sanitized = base + ext;
    }
    
    return sanitized;
};

const splitFilename = (filename) => {
    if (!filename) return { base: '', ext: '' };
    
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1 || lastDotIndex === 0) {
        return { base: filename, ext: '' };
    }
    
    return {
        base: filename.substring(0, lastDotIndex),
        ext: filename.substring(lastDotIndex)
    };
};

const inferMimeType = (filename) => {
    if (!filename) return null;
    
    const { ext } = splitFilename(filename);
    const extLower = ext.toLowerCase();
    
    const mimeMap = {
        '.jpg': 'image/jpeg',
        '.jpeg': 'image/jpeg',
        '.png': 'image/png',
        '.gif': 'image/gif',
        '.webp': 'image/webp',
        '.heic': 'image/heic',
        '.mp4': 'video/mp4',
        '.mov': 'video/quicktime',
        '.avi': 'video/x-msvideo',
        '.webm': 'video/webm',
        '.mp3': 'audio/mpeg',
        '.wav': 'audio/wav',
        '.wma': 'audio/x-ms-wma',
        '.m4a': 'audio/mp4',
        '.pdf': 'application/pdf',
        '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        '.doc': 'application/msword',
        '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        '.xls': 'application/vnd.ms-excel',
        '.csv': 'text/csv',
        '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        '.ppt': 'application/vnd.ms-powerpoint',
        '.zip': 'application/zip',
        '.txt': 'text/plain',
        '.rtf': 'application/rtf',
        '.json': 'application/json',
        '.html': 'text/html',
        '.css': 'text/css',
        '.js': 'text/javascript'
    };
    
    return mimeMap[extLower] || null;
};

const uploadFromUri = async ({ uri, file, key, contentType, onProgress = () => {}, publicBucket = false }) => {
    const orgID = await getCurrentOrgID();

    if (!key) return { error: 'Missing key' };

    if (!publicBucket && !key.startsWith(orgID + "/")) {
        key = orgID + "/" + key;
    }

    try {
        const bucket = publicBucket ? process.env.EXPO_PUBLIC_STORAGE_BUCKET_PUBLIC : process.env.EXPO_PUBLIC_STORAGE_BUCKET;

        // Use signed upload URL for all platforms to support files > 25MB (Supabase SDK direct upload limit)
        const { data: signedData, error: signedError } = await supabase.storage
            .from(bucket)
            .createSignedUploadUrl(key);

        if (signedError) {
            ErrorHandler(Error(signedError.message), { key, function: 'uploadFromUri' }, 'Failed to get upload URL');
            return { error: signedError };
        }

        if (Platform.OS === 'web') {
            // Web: use the raw File object from the picker if available (avoids re-fetching the
            // blob: URI which would duplicate the file in memory). Fall back to fetch for other URIs.
            const body = file instanceof Blob ? file : await fetch(uri).then(r => r.blob());
            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('PUT', signedData.signedUrl, true);
                xhr.setRequestHeader('Content-Type', contentType || body.type || 'application/octet-stream');
                xhr.upload.onprogress = (e) => {
                    if (e.lengthComputable) onProgress(e.loaded / e.total);
                };
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) resolve();
                    else reject(new Error(`Upload failed with status ${xhr.status}`));
                };
                xhr.onerror = () => reject(new Error('Network error during upload'));
                xhr.send(body);
            });
        } else {
            // Native: use XHR to bypass expo winter fetch polyfill, which doesn't support
            // { uri, type, name } body format and has Blob creation issues with ArrayBuffer on iOS.
            // XHR handles file:// URIs natively via RN and supports upload progress.
            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('PUT', signedData.signedUrl, true);
                xhr.setRequestHeader('Content-Type', contentType || 'application/octet-stream');
                xhr.upload.onprogress = (e) => {
                    if (e.lengthComputable) onProgress(e.loaded / e.total);
                };
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        onProgress(1);
                        resolve();
                    } else {
                        reject(new Error(`Upload failed with status ${xhr.status}`));
                    }
                };
                xhr.onerror = () => reject(new Error('Network error during upload'));
                xhr.send({ uri, type: contentType || 'application/octet-stream', name: key.split('/').pop() });
            });
        }

        return { error: null, key, storageUrl: (publicBucket ? storagePathPublic : storagePathPrivate) + '/' + key };
    } catch (error) {
        ErrorHandler(error, { key, contentType, function: 'uploadFromUri' }, 'Upload failed');
        return { error };
    }
};

const downloadToCache = async ({ key, filename }) => {
    const orgID = await getCurrentOrgID();
    
    if (Platform.OS === 'web') {
        // On web, return a signed URL instead of downloading
        const { data, error } = await supabase.storage
            .from(process.env.EXPO_PUBLIC_STORAGE_BUCKET)
            .createSignedUrl(key, 60);
        
        if (error) {
            ErrorHandler(error, { key, function: 'downloadToCache' }, 'Failed to create signed URL');
            return { error, uri: null };
        }
        
        return { error: null, uri: data.signedUrl };
    }
    
    if (!key.startsWith(orgID + "/")) {
        key = orgID + "/" + key;
    }
    
    const localKey = formatPath(key);
    const cacheDir = new Directory(Paths.cache, 'files');
    
    if (!cacheDir.exists) {
        cacheDir.create({ intermediates: true });
    }
    
    const destFile = new File(cacheDir, filename || localKey.split('/').pop());
    const downloadUrl = `${storagePathPrivate}/${key}`;

    // DestinationAlreadyExistsException happens when two downloads race the same
    // destination — our exists-check/delete runs before the native move. Delete
    // and retry once; if the file is already there after a failed retry, reuse it.
    let lastError = null;
    for (let attempt = 0; attempt < 2; attempt++) {
        try {
            if (destFile.exists) {
                destFile.delete();
            }

            await File.downloadFileAsync(downloadUrl, destFile, {
                headers: { 'Authorization': `Bearer ${_cachedAccessToken}` }
            });

            return { error: null, uri: destFile.uri };
        } catch (error) {
            lastError = error;
            if (!error?.message?.includes('DestinationAlreadyExists')) break;
        }
    }

    if (destFile.exists) {
        return { error: null, uri: destFile.uri };
    }

    ErrorHandler(lastError, { key, function: 'downloadToCache' }, 'Download failed');
    return { error: lastError, uri: null };
};

const shareFile = async ({ key, filename }) => {
    const { error, uri } = await downloadToCache({ key, filename });
    
    if (error) {
        Toast.show({ type: 'error', text2: 'Failed to prepare file for sharing' });
        return;
    }
    
    if (Platform.OS === 'web') {
        // On web, try navigator.share or trigger download
        if (navigator.share) {
            try {
                await navigator.share({
                    title: filename,
                    url: uri
                });
            } catch {
                // User cancelled or share failed
            }
        } else {
            // Fallback: trigger download
            const a = document.createElement('a');
            a.href = uri;
            a.download = filename;
            a.click();
        }
    } else {
        // Native: use expo-sharing
        const isAvailable = await Sharing.isAvailableAsync();
        
        if (isAvailable) {
            await Sharing.shareAsync(uri);
        } else {
            Toast.show({ type: 'error', text2: 'Sharing is not available on this device' });
        }
    }
};

const openFile = async ({ key, filename, mimeType: _mimeType }) => {
    const { error, uri } = await downloadToCache({ key, filename });
    
    if (error) {
        Toast.show({ type: 'error', text2: 'Failed to open file' });
        return;
    }
    
    if (Platform.OS === 'web') {
        // On web, open in new tab
        window.open(uri, '_blank');
    } else {
        // Native: use FileViewer
        try {
            await FileViewer.open(uri, {
                showOpenWithDialog: true,
                showAppsSuggestions: true
            });
        } catch (error) {
            console.debug('Error opening file:', error);
            Toast.show({ type: 'error', text2: 'No app found to open this file type' });
        }
    }
};

const pickDocument = async ({ multiple = false } = {}) => {
    const result = await DocumentPicker.getDocumentAsync({
        type: '*/*',
        multiple,
        copyToCacheDirectory: true,
    });

    if (result.canceled || !result.assets?.length) return [];

    return result.assets.map(asset => ({
        uri: asset.uri,
        file: asset.file || null,
        name: asset.name,
        filename: asset.name,
        size: asset.size || 0,
        mimeType: asset.mimeType || inferMimeType(asset.name) || 'application/octet-stream',
    }));
};

export {
    getImageSource,
    saveFile,
    saveStorageBlob,
    deleteLocalFile,
    getFile,
    removeStorageFile,
    getStorageFile,
    getPublicStorageFileURL,
    getSignedStorageFileURL,
    formatSize,
    clearFileCache,
    choosePhoto,
    renderManipulatedImage,
    getMediaFileInfo,
    storagePathPrivate,
    storagePathPublic,
    // New exports for Files v2
    sanitizeFilename,
    splitFilename,
    inferMimeType,
    uploadFromUri,
    downloadToCache,
    shareFile,
    openFile,
    pickDocument
}